import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Mutation } from '@apollo/client/react/components';
import { Route, Switch } from 'react-router-dom';
import { fromGlobalId } from 'graphql-relay';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import notifications from 'four-nets/icons/svg/icon_notifications.svg';
import addToGroup from 'four-nets/icons/svg/icon_add-to-group.svg';
import leaveIcon from 'four-nets/icons/svg/icon_logout.svg';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';

import joinGroup, { JoinGroupMutation, JoinGroupMutationVariables } from '../mutations/join-group.graphql';
import leaveGroup, { LeaveGroupMutation, LeaveGroupMutationVariables } from '../mutations/leave-group.graphql';
import subscribeGroup, {
    SubscribeGroupMutation,
    SubscribeGroupMutationVariables,
} from '../mutations/subscribe-group.graphql';
import unsubscribeGroup, {
    UnsubscribeGroupMutation,
    UnsubscribeGroupMutationVariables,
} from '../mutations/unsubscribe-group.graphql';
import defaultStyles from './group-main-button.scss';

export interface Props {
    styles: typeof defaultStyles;
    id: string;
    isJoinedByViewer: boolean;
    isSubscribedByViewer: boolean;
}

class GroupMainButton extends React.PureComponent<Props> {
    static contextTypes = {
        authCheck: PropTypes.func,
    };
    render() {
        const { styles, id, isJoinedByViewer, isSubscribedByViewer } = this.props;

        return (
            <Switch>
                <Route
                    path={`/group/:id/detail/`}
                    render={({ history }) => (
                        <Mutation<JoinGroupMutation, JoinGroupMutationVariables>
                            mutation={joinGroup}
                            optimisticResponse={{
                                joinGroup: {
                                    __typename: 'JoinGroupPayload',
                                    group: {
                                        __typename: 'GroupNode',
                                        id,
                                        isJoinedByViewer: true,
                                        isSubscribedByViewer: true,
                                    },
                                } as JoinGroupMutation['joinGroup'],
                            }}
                        >
                            {joinGroupFn => (
                                <Mutation<LeaveGroupMutation, LeaveGroupMutationVariables>
                                    mutation={leaveGroup}
                                    optimisticResponse={{
                                        leaveGroup: {
                                            __typename: 'LeaveGroupPayload',
                                            group: {
                                                __typename: 'GroupNode',
                                                id,
                                                isJoinedByViewer: false,
                                            },
                                        } as LeaveGroupMutation['leaveGroup'],
                                    }}
                                >
                                    {leaveGroupFn => (
                                        <button
                                            className={styles.button}
                                            onClick={() => {
                                                this.context.authCheck(() => {
                                                    const parsedId = fromGlobalId(id).id;
                                                    if (isJoinedByViewer) {
                                                        return leaveGroupFn({ variables: { input: { id } } }).then(() =>
                                                            history.push('/list-of-groups/')
                                                        );
                                                    } else {
                                                        return joinGroupFn({ variables: { input: { id } } }).then(() =>
                                                            history.push(`/group/${parsedId}/`)
                                                        );
                                                    }
                                                }, AuthOrigin.JoinGroup);
                                            }}
                                        >
                                            <SVGIcon
                                                style={{ image: styles.notificationsIcon }}
                                                src={isJoinedByViewer ? leaveIcon : addToGroup}
                                            />
                                            {isJoinedByViewer ? (
                                                <FormattedMessage id='LEAVE_GROUP' />
                                            ) : (
                                                <FormattedMessage id='JOIN_GROUP' />
                                            )}
                                        </button>
                                    )}
                                </Mutation>
                            )}
                        </Mutation>
                    )}
                />
                <Route
                    render={() => (
                        <Mutation<SubscribeGroupMutation, SubscribeGroupMutationVariables>
                            mutation={subscribeGroup}
                            optimisticResponse={{
                                subscribeGroup: {
                                    __typename: 'SubscribeGroupPayload',
                                    group: {
                                        __typename: 'GroupNode',
                                        id,
                                        isSubscribedByViewer: true,
                                    },
                                },
                            }}
                        >
                            {subscribeGroupFn => (
                                <Mutation<UnsubscribeGroupMutation, UnsubscribeGroupMutationVariables>
                                    mutation={unsubscribeGroup}
                                    optimisticResponse={{
                                        unsubscribeGroup: {
                                            __typename: 'UnsubscribeGroupPayload',
                                            group: {
                                                __typename: 'GroupNode',
                                                id,
                                                isSubscribedByViewer: false,
                                            },
                                        },
                                    }}
                                >
                                    {unsubscribeGroupFn =>
                                        isSubscribedByViewer ? (
                                            <button
                                                className={classnames(styles.button, styles.greenButton)}
                                                onClick={() => unsubscribeGroupFn({ variables: { input: { id } } })}
                                            >
                                                <SVGIcon
                                                    style={{ image: styles.notificationsIcon }}
                                                    src={notifications}
                                                />
                                                <FormattedMessage id='DISABLE_NOTIFICATIONS' />
                                            </button>
                                        ) : (
                                            <button
                                                className={classnames(styles.button, styles.greenButton)}
                                                onClick={() => subscribeGroupFn({ variables: { input: { id } } })}
                                            >
                                                <SVGIcon
                                                    style={{ image: styles.notificationsIcon }}
                                                    src={notifications}
                                                />
                                                <FormattedMessage id='ENABLE_NOTIFICATIONS' />
                                            </button>
                                        )
                                    }
                                </Mutation>
                            )}
                        </Mutation>
                    )}
                />
            </Switch>
        );
    }
}

export default Styled(defaultStyles)(GroupMainButton);
